//frontend/src/pages/auth/ResetPassword.jsx
import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { loginSettingsAPI, authAPI } from '../../services/api';
import '../../assets/styles/login.css';

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || '';
  
  // Redirect if no email
  useEffect(() => {
    if (!email) {
      navigate('/forgot-password');
    }
  }, [email, navigate]);
  
  // States
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(1); // 1: code, 2: password
  const [logoUrl, setLogoUrl] = useState('/logo.png');
  const [success, setSuccess] = useState(false);
  
  // Code input refs
  const inputRefs = useRef([]);

  // Fetch logo
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await loginSettingsAPI.getLoginSettings();
        if (response.success && response.data?.logoUrl) {
          let logo = response.data.logoUrl;
          if (typeof logo === 'string' && logo.startsWith('"')) {
            try { logo = JSON.parse(logo); } catch (e) {}
          }
          setLogoUrl(logo);
        }
      } catch (error) {
        console.error('Failed to fetch settings:', error);
      }
    };
    fetchSettings();
  }, []);

  const getLogoSrc = () => {
    if (!logoUrl) return '/logo.png';
    if (logoUrl.startsWith('http')) return logoUrl;
    if (logoUrl.startsWith('/uploads')) {
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5000/api/v1';
      return `${apiUrl.replace('/api/v1', '')}${logoUrl}`;
    }
    return logoUrl;
  };

  // Handle code input
  const handleCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    
    // Auto focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData.length === 6) {
      setCode(pastedData.split(''));
      inputRefs.current[5]?.focus();
    }
  };

  // Verify code
  const handleVerifyCode = async () => {
    setError('');
    const codeString = code.join('');
    
    if (codeString.length !== 6) {
      setError('6 оронтой код оруулна уу');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authAPI.verifyResetCode(email, codeString);
      
      if (response.data.success) {
        setStep(2);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Код буруу байна');
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async () => {
    setError('');
    
    if (!newPassword) {
      setError('Шинэ нууц үг оруулна уу');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Нууц үг хамгийн багадаа 6 тэмдэгт');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Нууц үг таарахгүй байна');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await authAPI.resetPassword(email, code.join(''), newPassword);
      
      if (response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  // Success Screen
  if (success) {
    return (
      <div className="login-container forgot-container">
        <div className="forgot-left">
          <div className="forgot-circle circle-1"></div>
          <div className="forgot-circle circle-2"></div>
          <div className="forgot-circle circle-3"></div>
          <div className="forgot-left-content">
            <div className="forgot-icon">✅</div>
            <h2>Амжилттай!</h2>
            <p>Нууц үг шинэчлэгдлээ</p>
          </div>
        </div>
        
        <div className="login-form-section">
          <div className="login-form-container">
            <div className="success-container">
              <div className="success-icon">✅</div>
              <h2>Нууц үг амжилттай шинэчлэгдлээ!</h2>
              <p>Нэвтрэх хуудас руу шилжиж байна...</p>
              <div className="spinner"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container forgot-container">
      {/* Left Side - Blue */}
      <div className="forgot-left">
        <div className="forgot-circle circle-1"></div>
        <div className="forgot-circle circle-2"></div>
        <div className="forgot-circle circle-3"></div>
        
        <div className="forgot-left-content">
          <div className="forgot-icon">{step === 1 ? '📧' : '🔑'}</div>
          <h2>{step === 1 ? 'Код баталгаажуулах' : 'Шинэ нууц үг'}</h2>
          <p>
            {step === 1 
              ? `${email} хаяг руу код илгээгдлээ`
              : 'Шинэ нууц үгээ оруулна уу'
            }
          </p>
        </div>
      </div>
      
      {/* Right Side - Form */}
      <div className="login-form-section">
        <div className="login-form-container">
          {/* Header */}
          <div className="form-header">
            <div className="form-header-logo">
              <img src={getLogoSrc()} alt="Logo" />
            </div>
            <h1 className="form-title">
              {step === 1 ? 'Код оруулах' : 'Шинэ нууц үг'}
            </h1>
            <p className="form-subtitle">
              {step === 1 
                ? 'И-мэйл рүү илгээсэн 6 оронтой кодыг оруулна уу'
                : 'Шинэ нууц үгээ оруулна уу'
              }
            </p>
          </div>
          
          {/* Email badge */}
          <div className="email-badge">
            <span>📧</span>
            <span>{email}</span>
          </div>
          
          {/* Error */}
          {error && (
            <div className="error-message">
              <span className="error-icon">⚠️</span>
              <div className="error-content">
                <p>{error}</p>
              </div>
            </div>
          )}
          
          {/* Step 1: Code Input */}
          {step === 1 && (
            <>
              <div className="code-input-container">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    ref={el => inputRefs.current[index] = el}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="code-input"
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              
              <button
                onClick={handleVerifyCode}
                disabled={loading || code.join('').length !== 6}
                className="login-btn"
              >
                <span className="login-btn-content">
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Шалгаж байна...
                    </>
                  ) : (
                    'Баталгаажуулах'
                  )}
                </span>
              </button>
              
              <p className="resend-text">
                Код ирээгүй юу?{' '}
                <Link to="/forgot-password" className="resend-link">
                  Дахин илгээх
                </Link>
              </p>
            </>
          )}
          
          {/* Step 2: New Password */}
          {step === 2 && (
            <>
              <div className="input-group">
                <label className="input-label" htmlFor="newPassword">
                  Шинэ нууц үг
                </label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Хамгийн багадаа 6 тэмдэгт"
                    className="login-input"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="password-toggle"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              
              <div className="input-group">
                <label className="input-label" htmlFor="confirmPassword">
                  Нууц үг давтах
                </label>
                <div className="input-wrapper">
                  <span className="input-icon">🔒</span>
                  <input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Нууц үгээ дахин оруулна уу"
                    className="login-input"
                  />
                </div>
              </div>
              
              <button
                onClick={handleResetPassword}
                disabled={loading}
                className="login-btn"
              >
                <span className="login-btn-content">
                  {loading ? (
                    <>
                      <span className="spinner"></span>
                      Шинэчилж байна...
                    </>
                  ) : (
                    'Нууц үг шинэчлэх'
                  )}
                </span>
              </button>
            </>
          )}
          
          {/* Back to Login */}
          <div className="register-row">
            <Link to="/login" className="back-to-login">
              ← Нэвтрэх хуудас руу буцах
            </Link>
          </div>
          
          {/* Footer */}
          <div className="login-footer">
            <p>© {new Date().getFullYear()} Pingo Express</p>
            <p className="developed-by">
              Developed by{' '}
              <a href="mailto:info@prolink.mn" className="prolink-link">
                Prolink Team
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;